import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3,
  Send,
  Mail,
  CheckCircle,
  Truck,
  RotateCcw,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  Loader2,
  AlertCircle,
  ChevronDown,
  ArrowUpRight,
  PieChart
} from "lucide-react";
import {
  fetchCampaignAnalytics,
  fetchMailingLists,
  formatCurrency,
  formatDate,
  CAMPAIGN_STATUS_CONFIG
} from "../../services/MailingListService";

/**
 * CampaignAnalytics - Comprehensive analytics dashboard for direct mail campaigns
 */
const CampaignAnalytics = ({ userEmail, userMeta }) => {
  const [analytics, setAnalytics] = useState(null);
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedListId, setSelectedListId] = useState("");
  const [selectedDays, setSelectedDays] = useState(30);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [analyticsData, listsData] = await Promise.all([
        fetchCampaignAnalytics({ list_id: selectedListId || undefined, days: selectedDays }),
        lists.length === 0 ? fetchMailingLists() : Promise.resolve({ lists })
      ]);

      setAnalytics(analyticsData);
      if (listsData.lists) {
        setLists(listsData.lists);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedListId, selectedDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Metric card component
  const MetricCard = ({ icon: Icon, label, value, subValue, trend, color = "sky", large = false }) => (
    <div className={`p-${large ? 6 : 4} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            trend >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
          }`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className={`${large ? "text-3xl" : "text-2xl"} font-bold dark:text-white text-slate-900`}>{value}</div>
      {subValue && (
        <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subValue}</div>
      )}
    </div>
  );

  // Simple bar chart for daily volume
  const DailyVolumeChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="h-40 flex items-center justify-center text-slate-400 dark:text-slate-500">
          No data for selected period
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.sent), 1);

    return (
      <div className="h-40 flex items-end gap-1">
        {data.map((day, index) => {
          const height = (day.sent / maxValue) * 100;
          const deliveredHeight = (day.delivered / maxValue) * 100;

          return (
            <div
              key={day.date}
              className="flex-1 flex flex-col items-center group relative"
            >
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="bg-slate-800 dark:bg-slate-700 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap">
                  <div>{formatDate(day.date)}</div>
                  <div>Sent: {day.sent}</div>
                  <div>Delivered: {day.delivered}</div>
                </div>
              </div>

              {/* Bars */}
              <div className="w-full flex flex-col items-center gap-0.5">
                <div
                  className="w-full bg-sky-500 dark:bg-sky-600 rounded-t-sm transition-all"
                  style={{ height: `${height}%`, minHeight: day.sent > 0 ? "4px" : "0" }}
                />
                <div
                  className="w-full bg-green-500 dark:bg-green-600 rounded-b-sm transition-all"
                  style={{ height: `${deliveredHeight}%`, minHeight: day.delivered > 0 ? "2px" : "0" }}
                />
              </div>

              {/* Date label (show every 7th) */}
              {index % 7 === 0 && (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 truncate w-full text-center">
                  {day.date.slice(5)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Status breakdown component
  const StatusBreakdown = ({ data }) => {
    const total = Object.values(data).reduce((sum, count) => sum + count, 0);
    if (total === 0) return null;

    const statusColors = {
      draft: "bg-slate-400",
      sending: "bg-amber-500",
      sent: "bg-blue-500",
      partial: "bg-orange-500",
      failed: "bg-red-500",
      cancelled: "bg-slate-300"
    };

    return (
      <div className="space-y-3">
        {Object.entries(data).map(([status, count]) => {
          const percentage = (count / total) * 100;
          const config = CAMPAIGN_STATUS_CONFIG[status] || { label: status };

          return (
            <div key={status} className="flex items-center gap-3">
              <div className="w-24 text-sm text-slate-600 dark:text-slate-400">
                {config.label}
              </div>
              <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${statusColors[status] || "bg-slate-400"} transition-all`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="w-12 text-right text-sm font-medium dark:text-white text-slate-900">
                {count}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // List performance component
  const ListPerformance = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-8 text-slate-400 dark:text-slate-500">
          No list data available
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {data.slice(0, 5).map((list) => {
          const listInfo = lists.find(l => l.id === list.list_id);
          const listName = listInfo?.name || list.list_id.slice(0, 12) + "...";

          return (
            <div
              key={list.list_id}
              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium dark:text-white text-slate-900 truncate">
                  {listName}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {list.campaigns} campaigns · {list.sent.toLocaleString()} sent
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-green-600 dark:text-green-400">
                  {list.delivery_rate.toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {formatCurrency(list.spend)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Top campaigns component
  const TopCampaigns = ({ campaigns }) => {
    if (!campaigns || campaigns.length === 0) {
      return (
        <div className="text-center py-8 text-slate-400 dark:text-slate-500">
          No recent campaigns
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {campaigns.map((campaign, index) => {
          const deliveryRate = campaign.metrics.sent > 0
            ? (campaign.metrics.delivered / campaign.metrics.sent * 100).toFixed(1)
            : 0;

          return (
            <div
              key={campaign.id}
              className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
            >
              <div className="w-6 h-6 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-xs font-semibold text-sky-600 dark:text-sky-400">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium dark:text-white text-slate-900 truncate">
                  {campaign.name}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {formatDate(campaign.sent_at)} · {campaign.metrics.sent.toLocaleString()} sent
                </div>
              </div>
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                <CheckCircle className="w-4 h-4" />
                {deliveryRate}%
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Loading state
  if (isLoading && !analytics) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error && !analytics) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            Failed to load analytics
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!analytics || analytics.summary.total_campaigns === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
            No Campaign Data
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Send your first campaign to see analytics here.
          </p>
        </div>
      </div>
    );
  }

  const { summary, by_status, by_list, daily_volume, top_campaigns } = analytics;

  return (
    <div className="p-6 space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selectedListId}
          onChange={(e) => setSelectedListId(e.target.value)}
          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm"
        >
          <option value="">All Mailing Lists</option>
          {lists.map((list) => (
            <option key={list.id} value={list.id}>
              {list.name}
            </option>
          ))}
        </select>

        <select
          value={selectedDays}
          onChange={(e) => setSelectedDays(parseInt(e.target.value))}
          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>

        {isLoading && (
          <Loader2 className="w-4 h-4 text-sky-600 animate-spin" />
        )}
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <MetricCard
          icon={Send}
          label="Total Campaigns"
          value={summary.total_campaigns}
          color="sky"
        />
        <MetricCard
          icon={Mail}
          label="Postcards Sent"
          value={summary.total_sent.toLocaleString()}
          color="blue"
        />
        <MetricCard
          icon={CheckCircle}
          label="Delivered"
          value={summary.total_delivered.toLocaleString()}
          subValue={`${summary.delivery_rate}% rate`}
          color="green"
        />
        <MetricCard
          icon={Truck}
          label="In Transit"
          value={summary.total_in_transit.toLocaleString()}
          color="amber"
        />
        <MetricCard
          icon={RotateCcw}
          label="Returned"
          value={summary.total_returned.toLocaleString()}
          subValue={`${summary.return_rate}% rate`}
          color="red"
        />
        <MetricCard
          icon={DollarSign}
          label="Total Spend"
          value={formatCurrency(summary.total_spend)}
          color="emerald"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily volume chart */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-sky-600" />
              Daily Volume
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-sky-500" />
                <span className="text-slate-500 dark:text-slate-400">Sent</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                <span className="text-slate-500 dark:text-slate-400">Delivered</span>
              </div>
            </div>
          </div>
          <DailyVolumeChart data={daily_volume} />
        </div>

        {/* Status breakdown */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
          <h3 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-sky-600" />
            Campaign Status
          </h3>
          <StatusBreakdown data={by_status} />
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* List performance */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
          <h3 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-sky-600" />
            List Performance
          </h3>
          <ListPerformance data={by_list} />
        </div>

        {/* Top campaigns */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
          <h3 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <ArrowUpRight className="w-5 h-5 text-sky-600" />
            Top Performing Campaigns
          </h3>
          <TopCampaigns campaigns={top_campaigns} />
        </div>
      </div>

      {/* Info footer */}
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-sky-600 mt-0.5" />
          <div>
            <h4 className="font-medium dark:text-white text-slate-900">Real-Time Tracking</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Delivery status updates automatically via Lob webhooks. Analytics refresh when you change filters.
              Postcards typically take 3-5 business days for delivery.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
